// src/pages/Agencies.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  getAgencies, 
  partnerSendQuery, 
  getReceivedAgencyRequests,
  acceptAgencyRequest,
  rejectAgencyRequest,
  getSentQueries 
} from "../api/agencies";
import { isAuthenticated, getStoredUser } from "../api/auth";
import { createRating } from "../api/ratings";
import { triggerNotificationRefresh } from "../utils/notificationRefresh";
import CustomAlert from "../components/CustomAlert";
import RatingModal from "../components/RatingModal";
import RatingsViewModal from "../components/RatingsViewModal";
import "./Agencies.css";

export default function Agencies() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [activeTab, setActiveTab] = useState("agencies"); // agencies, requests, sent
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [sentQueries, setSentQueries] = useState([]); // Partner's sent queries
  
  // Query modal
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [queryMessage, setQueryMessage] = useState("");
  const [sendingQuery, setSendingQuery] = useState(false);

  // Rating modal
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null); // {userId, userName, userRole}

  // Ratings view modal
  const [showRatingsViewModal, setShowRatingsViewModal] = useState(false);
  const [ratingsViewTarget, setRatingsViewTarget] = useState(null); // {userId, userName}

  // Custom Alert state
  const [alertState, setAlertState] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showAlert = (title, message, type = 'info') => {
    setAlertState({ show: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertState({ ...alertState, show: false });
  };

  useEffect(() => {
    // Check authentication first
    const authenticated = isAuthenticated();
    setIsAuth(authenticated);
    
    if (authenticated) {
      const user = getStoredUser();
      const role = user?.role || "";
      setUserRole(role);
      
      // Redirect agency users away from this page
      if (role === "agency") {
        navigate("/find-partner");
        return;
      }
      
      loadAgencies();
      
      // If partner, load agency requests and sent queries
      if (role === "partner") {
        loadAgencyRequests();
        loadSentQueries();
      }
    } else {
      setLoading(false);
    }
  }, [navigate]);

  // Add body class when query modal is open to disable background interactions
  useEffect(() => {
    if (showQueryModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => document.body.classList.remove('modal-open');
  }, [showQueryModal]);

  useEffect(() => {
    filterAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencies, searchTerm, selectedLocation]);

  async function loadAgencies() {
    try {
      setLoading(true);
      const data = await getAgencies();
      setAgencies(data);
      setFilteredAgencies(data);
      setError(null);
    } catch (err) {
      console.error("Error loading agencies:", err);
      setError("Failed to load agencies. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function loadAgencyRequests() {
    try {
      const requests = await getReceivedAgencyRequests();
      setAgencyRequests(requests);
    } catch (err) {
      console.error("Error loading agency requests:", err);
    }
  }

  async function loadSentQueries() {
    try {
      const queries = await getSentQueries();
      setSentQueries(queries);
    } catch (err) {
      console.error("Error loading sent queries:", err);
    }
  }

  async function handleAcceptAgencyRequest(requestId) {
    try {
      await acceptAgencyRequest(requestId);
      showAlert(
        "Request Accepted!",
        "You've connected with this agency. They can now view your contact details.",
        "success"
      );
      loadAgencyRequests();
    } catch (err) {
      console.error("Error accepting request:", err);
      showAlert(
        "Failed to Accept",
        err.response?.data?.detail || "Unable to accept request. Please try again.",
        "error"
      );
    }
  }

  async function handleRejectAgencyRequest(requestId) {
    try {
      await rejectAgencyRequest(requestId);
      showAlert(
        "Request Rejected",
        "The agency request has been rejected.",
        "info"
      );
      loadAgencyRequests();
    } catch (err) {
      console.error("Error rejecting request:", err);
      showAlert(
        "Failed to Reject",
        err.response?.data?.detail || "Unable to reject request. Please try again.",
        "error"
      );
    }
  }

  async function handleRateUser(userId, userName, userRole) {
    setRatingTarget({ userId, userName, userRole });
    setShowRatingModal(true);
  }

  async function handleSubmitRating(rating, feedbackMessage) {
    try {
      await createRating(ratingTarget.userId, rating, feedbackMessage);
      showAlert(
        'Rating Submitted! ⭐', 
        `Thank you for rating ${ratingTarget.userName}! Your feedback helps the community.`, 
        'success'
      );
      triggerNotificationRefresh();
      // Reload data to reflect the new rating
      loadAgencies();
      if (userRole === "partner") {
        loadAgencyRequests();
        loadSentQueries();
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
      const errorMessage = err.response?.data?.detail || "Failed to submit rating. Please try again.";
      showAlert('Rating Failed', errorMessage, 'error');
    }
  }

  function handleViewRatings(userId, userName) {
    // Prevent opening ratings while query modal is open
    if (showQueryModal) return;
    setRatingsViewTarget({ userId, userName });
    setShowRatingsViewModal(true);
  }

  function filterAgencies() {
    let filtered = agencies;

    if (searchTerm) {
      filtered = filtered.filter(agency =>
        agency.agency_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.services.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLocation !== "all") {
      filtered = filtered.filter(agency =>
        agency.location.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    setFilteredAgencies(filtered);
  }

  const locations = ["all", ...new Set(agencies.map(a => a.location))];

  function openQueryModal(agency) {
    setSelectedAgency(agency);
    setQueryMessage(`Hi! I'm interested in your travel services. I'm planning a trip and would love to discuss package options.`);
    setShowQueryModal(true);
  }

  async function handleSendQuery() {
    if (!queryMessage.trim()) {
      showAlert("Empty Message", "Please enter a message before sending your query.", "warning");
      return;
    }

    try {
      setSendingQuery(true);
      // Partner sends query to agency using correct API
      await partnerSendQuery(selectedAgency.id, queryMessage);
      setShowQueryModal(false);
      setQueryMessage("");
      showAlert(
        "Query Sent Successfully!", 
        `Your query has been sent to ${selectedAgency.agency_name}. They will contact you soon via your registered email or phone.`,
        "success"
      );
      // Reload sent queries to update the list
      loadSentQueries();
      // Reload agencies to update query status
      loadAgencies();
    } catch (err) {
      console.error("Error sending query:", err);
      showAlert(
        "Failed to Send Query",
        err.response?.data?.detail || "Unable to send query. Please make sure you're logged in and try again.",
        "error"
      );
    } finally {
      setSendingQuery(false);
    }
  }

  // Check if partner has already sent a query to this agency
  function hasExistingQuery(agencyId) {
    // First check if the agency itself has a connection_status
    const agency = filteredAgencies.find(a => a.id === agencyId);
    if (agency?.connection_status && agency.connection_status !== 'none') {
      return true;
    }
    
    // Also check sentQueries array
    return sentQueries.some(query => 
      query.agency_id === agencyId && 
      (query.status === 'pending' || query.status === 'responded' || query.status === 'accepted')
    );
  }

  // Get the query for a specific agency to show status
  function getQueryForAgency(agencyId) {
    // First check if the agency itself has a connection_status
    const agency = filteredAgencies.find(a => a.id === agencyId);
    if (agency?.connection_status && agency.connection_status !== 'none') {
      // Return a mock query object with the connection status
      // Check if current user is the sender (partner sending to agency)
      return {
        status: agency.connection_status,
        agency_id: agencyId,
        is_sender: agency.is_sender !== undefined ? agency.is_sender : true // Default to true for backward compatibility
      };
    }
    
    // Fall back to sentQueries array
    return sentQueries.find(query => 
      query.agency_id === agencyId && 
      (query.status === 'pending' || query.status === 'responded' || query.status === 'accepted')
    );
  }

  // Get the display text for query status
  function getQueryStatusText(query) {
    if (!query) return 'Send Query';
    
    // If the current user is NOT the sender, show "Query Received"
    if (query.is_sender === false) {
      return query.status === 'accepted' ? 'Request Accepted' : 
             query.status === 'responded' ? 'Request Responded' : 
             'Request Received';
    }
    
    // If the current user IS the sender, show "Query Sent"
    return query.status === 'accepted' ? 'Query Accepted' : 
           query.status === 'responded' ? 'Agency Responded' : 
           'Query Sent';
  }

  // Get the icon for query status
  function getQueryStatusIcon(query) {
    if (!query) return '💬';
    
    if (query.is_sender === false) {
      return query.status === 'accepted' ? '✅' : '📨';
    }
    
    return query.status === 'accepted' ? '✅' : '✓';
  }

  return (
    <div className="agencies-container">
      {/* Header Section */}
      <div className="agencies-header">
        <div className="header-content">
          <h1 className="page-title">Travel Agencies</h1>
          <p className="page-subtitle">
            Discover trusted travel agencies offering amazing group tours and customized trips
          </p>
        </div>
      </div>

      {/* Check if user is authenticated */}
      {!isAuth ? (
        <div className="auth-required-section">
          <div className="container">
            <div className="auth-required-card">
              <div className="auth-icon">🔒</div>
              <h2>Authentication Required</h2>
              <p>Please login or register to view travel agencies and send queries for your trip planning.</p>
              <div className="auth-actions">
                <Link to="/login" className="auth-btn login-btn">
                  <span>Login</span>
                  <span className="btn-icon">→</span>
                </Link>
                <Link to="/register" className="auth-btn register-btn">
                  <span>Register</span>
                  <span className="btn-icon">✓</span>
                </Link>
              </div>
              <div className="auth-benefits">
                <h3>Why Register?</h3>
                <ul>
                  <li>🏢 Browse verified travel agencies</li>
                  <li>💬 Send queries directly to agencies</li>
                  <li>📞 Get instant contact information</li>
                  <li>✈️ Plan your perfect trip with experts</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs Section - Only for Partners */}
          {userRole === "partner" && (
            <div className="tabs-section">
              <div className="container">
                <div className="tabs-wrapper">
                  <button
                    className={`tab-button ${activeTab === "agencies" ? "active" : ""}`}
                    onClick={() => setActiveTab("agencies")}
                  >
                    <span className="tab-icon">🏢</span>
                    <span>Browse Agencies</span>
                    <span className="tab-count">{filteredAgencies.length}</span>
                  </button>
                  <button
                    className={`tab-button ${activeTab === "requests" ? "active" : ""}`}
                    onClick={() => setActiveTab("requests")}
                  >
                    <span className="tab-icon">📬</span>
                    <span>Agency Requests</span>
                    {agencyRequests.filter(r => r.status === 'pending').length > 0 && (
                      <span className="tab-badge">{agencyRequests.filter(r => r.status === 'pending').length}</span>
                    )}
                  </button>
                  <button
                    className={`tab-button ${activeTab === "sent" ? "active" : ""}`}
                    onClick={() => setActiveTab("sent")}
                  >
                    <span className="tab-icon">📤</span>
                    <span>Sent Queries</span>
                    {sentQueries.filter(q => q.status === 'pending').length > 0 && (
                      <span className="tab-badge">{sentQueries.filter(q => q.status === 'pending').length}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Agencies Tab Content */}
          {activeTab === "agencies" && (
            <>
          {/* Filters Section */}
          <div className="filters-section">
        <div className="container">
          <div className="filters-wrapper">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search agencies, locations, or services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Location:</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="filter-select"
              >
                {locations.map(loc => (
                  <option key={loc} value={loc}>
                    {loc === "all" ? "All Locations" : loc}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Agencies Grid */}
      <div className="agencies-section">
        <div className="container">
          {loading ? (
            <div className="loading">Loading agencies...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : filteredAgencies.length > 0 ? (
            <>
              <div className="results-header">
                <h2 className="results-title">
                  Featured Agencies
                  <span className="results-count">({filteredAgencies.length} found)</span>
                </h2>
              </div>
              
              <div className="agencies-grid">
                {filteredAgencies.map((agency) => (
                  <div key={agency.id} className="agency-card">
                    <div className="agency-image-wrapper">
                      <img 
                        src={agency.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(agency.agency_name)}&background=random&size=400`} 
                        alt={agency.agency_name} 
                        className="agency-image" 
                      />
                    </div>
                    
                    <div className="agency-body">
                      <div className="agency-header-info">
                        <h3 className="agency-name">{agency.agency_name}</h3>

                        <div className="agency-meta">
                          {/* Rating Display - Clickable (stars + value) */}
                          {agency.average_rating > 0 && (
                            <div 
                              className={`agency-rating clickable-rating ${showQueryModal ? 'disabled-rating' : ''}`}
                              onClick={!showQueryModal ? () => handleViewRatings(agency.user.id, agency.agency_name) : undefined}
                              title={!showQueryModal ? "Click to view all ratings" : ""}
                            >
                              <span className="rating-stars">
                                {'⭐'.repeat(Math.round(agency.average_rating))}
                              </span>
                              <span className="rating-value">
                                {agency.average_rating.toFixed(1)}
                              </span>
                            </div>
                          )}

                          {/* Move the count out into its own visible badge */}
                          {agency.total_ratings !== undefined && (
                            <span className="rating-badge">{agency.total_ratings} review{agency.total_ratings !== 1 ? 's' : ''}</span>
                          )}

                          <div className="agency-location">
                            <span className="location-icon">📍</span>
                            {agency.location}
                          </div>
                        </div>

                      </div>

                      <p className="agency-description">{agency.description}</p>

                      <div className="agency-services">
                        <div className="services-label">Services:</div>
                        <div className="services-text">{agency.services}</div>
                      </div>

                      {/* Contact Info - Always Visible for Agencies */}
                      <div className="agency-contact-info">
                        <div className="contact-item">
                          <span className="contact-icon">📧</span>
                          <a href={`mailto:${agency.user.email}`} className="contact-link">
                            {agency.user.email}
                          </a>
                        </div>
                        <div className="contact-item">
                          <span className="contact-icon">📱</span>
                          <a href={`tel:${agency.user.phone}`} className="contact-link">
                            {agency.user.phone}
                          </a>
                        </div>
                        {agency.website && (
                          <div className="contact-item">
                            <span className="contact-icon">🌐</span>
                            <a href={agency.website} target="_blank" rel="noopener noreferrer" className="contact-link">
                              Visit Website
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="agency-footer">
                      {hasExistingQuery(agency.id) ? (
                        <>
                          <button 
                            className={`btn-query-sent ${getQueryForAgency(agency.id)?.status === 'accepted' ? 'accepted' : ''}`}
                            disabled
                          >
                            <span>
                              {getQueryStatusText(getQueryForAgency(agency.id))}
                            </span>
                            <span className="btn-icon">
                              {getQueryStatusIcon(getQueryForAgency(agency.id))}
                            </span>
                          </button>
                          
                          {/* Show rate button only if query is accepted and not already rated */}
                          {getQueryForAgency(agency.id)?.status === 'accepted' && (
                            agency.my_rating ? (
                              <div className="already-rated-badge">
                                ✓ Rated: {'⭐'.repeat(agency.my_rating)}
                              </div>
                            ) : (
                              <button 
                                className="rate-btn"
                                onClick={() => handleRateUser(agency.user.id, agency.agency_name, 'agency')}
                                title={`Rate ${agency.agency_name}`}
                              >
                                <span className="btn-icon">⭐</span>
                                Rate Agency
                              </button>
                            )
                          )}
                        </>
                      ) : (
                        <button 
                          className="btn-send-query"
                          onClick={() => openQueryModal(agency)}
                        >
                          <span>Send Query</span>
                          <span className="btn-icon">💬</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🔍🏢🌴</div>
              <h2 className="no-results-title">No Agencies Found</h2>
              <p className="no-results-text">
                We couldn't find any travel agencies matching your search criteria. 
                Try adjusting your filters to discover amazing travel packages!
              </p>
              <div className="no-results-suggestions">
                <h3>💡 Try these tips:</h3>
                <ul>
                  <li>🔍 Use different search keywords</li>
                  <li>📍 Select "All Locations" to broaden your search</li>
                  <li>✨ Clear filters and start fresh</li>
                  <li>⏰ Check back later for new agency listings</li>
                </ul>
              </div>
              <button 
                className="btn-clear-filters"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedLocation("all");
                }}
              >
                🔄 Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
            </>
          )}

          {/* Requests Tab Content - Partner View */}
          {activeTab === "requests" && userRole === "partner" && (
            <div className="requests-section">
              <div className="container">
                <div className="requests-header">
                  <h2 className="requests-title">
                    Agency Requests
                    <span className="results-count">({agencyRequests.length} total)</span>
                  </h2>
                  <p className="requests-subtitle">
                    Travel agencies have shown interest in helping with your trips
                  </p>
                </div>

                {agencyRequests.length === 0 ? (
                  <div className="no-results">
                    <div className="no-results-icon">📬✨</div>
                    <h2 className="no-results-title">No Agency Requests Yet</h2>
                    <p className="no-results-text">
                      When travel agencies express interest in helping with your trips, 
                      their requests will appear here.
                    </p>
                    <div className="no-results-suggestions">
                      <h3>💡 Next Steps:</h3>
                      <ul>
                        <li>✈️ Create trips in "My Trips" to attract agencies</li>
                        <li>🏢 Browse agencies and send queries</li>
                        <li>📍 Update your profile with travel preferences</li>
                        <li>🔔 Check back regularly for new requests</li>
                      </ul>
                    </div>
                    <button 
                      className="btn-clear-filters"
                      onClick={() => setActiveTab("agencies")}
                    >
                      🏢 Browse Agencies
                    </button>
                  </div>
                ) : (
                  <div className="requests-grid">
                    {agencyRequests.map((request) => (
                      <div key={request.id} className={`request-card status-${request.status}`}>
                        <div className="request-header">
                          <div className="agency-info">
                            <img 
                              src={request.agency?.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(request.agency?.agency_name || 'Agency')}&background=random&size=100`} 
                              alt={request.agency?.agency_name} 
                              className="agency-logo-small" 
                            />
                            <div className="agency-details">
                              <h3 className="agency-name-small">{request.agency?.agency_name}</h3>
                              <div className="agency-location-small">
                                <span className="location-icon">📍</span>
                                {request.agency?.location}
                              </div>
                            </div>
                          </div>
                          <div className={`status-badge status-${request.status}`}>
                            {request.status === 'pending' && '⏳ Pending'}
                            {request.status === 'accepted' && '✅ Accepted'}
                            {request.status === 'rejected' && '❌ Rejected'}
                          </div>
                        </div>

                        <div className="request-message">
                          <div className="message-label">📨 Message:</div>
                          <p className="message-text">{request.query_message}</p>
                        </div>

                        <div className="request-contact">
                          <div className="contact-item-small">
                            <span className="contact-icon">📧</span>
                            <a href={`mailto:${request.agency?.user?.email}`} className="contact-link-small">
                              {request.agency?.user?.email}
                            </a>
                          </div>
                          {request.agency?.user?.phone && (
                            <div className="contact-item-small">
                              <span className="contact-icon">📱</span>
                              <a href={`tel:${request.agency?.user?.phone}`} className="contact-link-small">
                                {request.agency?.user?.phone}
                              </a>
                            </div>
                          )}
                          {request.agency?.website && (
                            <div className="contact-item-small">
                              <span className="contact-icon">🌐</span>
                              <a href={request.agency?.website} target="_blank" rel="noopener noreferrer" className="contact-link-small">
                                Visit Website
                              </a>
                            </div>
                          )}
                        </div>

                        {request.status === 'pending' && (
                          <div className="request-actions">
                            <button 
                              className="btn-accept"
                              onClick={() => handleAcceptAgencyRequest(request.id)}
                            >
                              <span className="btn-icon">✅</span>
                              Accept
                            </button>
                            <button 
                              className="btn-reject"
                              onClick={() => handleRejectAgencyRequest(request.id)}
                            >
                              <span className="btn-icon">❌</span>
                              Reject
                            </button>
                          </div>
                        )}

                        {request.status === 'accepted' && (
                          <div className="request-actions">
                            {request.agency?.my_rating ? (
                              <div className="already-rated-badge">
                                ✓ Rated: {'⭐'.repeat(request.agency.my_rating)}
                              </div>
                            ) : (
                              <button 
                                className="rate-btn"
                                onClick={() => handleRateUser(
                                  request.agency?.user?.id,
                                  request.agency?.agency_name,
                                  'agency'
                                )}
                                title={`Rate ${request.agency?.agency_name}`}
                              >
                                <span className="btn-icon">⭐</span>
                                Rate Agency
                              </button>
                            )}
                          </div>
                        )}

                        <div className="request-date">
                          Received: {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}

                    {/* Pagination controls */}
                    <div className="pagination">
                      <button className="prev-page" disabled>
                        ← Previous
                      </button>
                      <button className="next-page">
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sent Queries Tab Content - Partner View */}
          {activeTab === "sent" && userRole === "partner" && (
            <div className="requests-section">
              <div className="container">
                <div className="requests-header">
                  <h2 className="requests-title">
                    Sent Queries
                    <span className="results-count">({sentQueries.length} total)</span>
                  </h2>
                  <p className="requests-subtitle">
                    View the queries you've sent to travel agencies
                  </p>
                </div>

                {sentQueries.length === 0 ? (
                  <div className="no-results">
                    <div className="no-results-icon">📤✨</div>
                    <h2 className="no-results-title">No Queries Sent Yet</h2>
                    <p className="no-results-text">
                      You haven't sent any queries to travel agencies yet. 
                      Browse agencies and send queries to get travel packages and information.
                    </p>
                    <div className="no-results-suggestions">
                      <h3>💡 Next Steps:</h3>
                      <ul>
                        <li>🏢 Browse agencies and explore their services</li>
                        <li>💬 Send queries about your travel plans</li>
                        <li>✈️ Get personalized travel packages</li>
                        <li>🔔 Agencies will respond to your queries</li>
                      </ul>
                    </div>
                    <button 
                      className="btn-clear-filters"
                      onClick={() => setActiveTab("agencies")}
                    >
                      🏢 Browse Agencies
                    </button>
                  </div>
                ) : (
                  <div className="requests-grid">
                    {sentQueries.map((query) => (
                      <div key={query.id} className={`request-card status-${query.status}`}>
                        <div className="request-header">
                          <div className="agency-info">
                            <img 
                              src={query.agency?.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(query.agency?.agency_name || 'Agency')}&background=random&size=100`} 
                              alt={query.agency?.agency_name} 
                              className="agency-logo-small" 
                            />
                            <div className="agency-details">
                              <h3 className="agency-name-small">{query.agency?.agency_name || 'Agency'}</h3>
                              <div className="agency-location-small">
                                <span className="location-icon">📍</span>
                                {query.agency?.location || 'Location not available'}
                              </div>
                            </div>
                          </div>
                          <div className={`status-badge status-${query.status}`}>
                            {query.status === 'pending' && '⏳ Pending'}
                            {query.status === 'accepted' && '✅ Accepted'}
                            {query.status === 'responded' && '💬 Responded'}
                            {query.status === 'rejected' && '❌ Rejected'}
                            {query.status === 'closed' && '🔒 Closed'}
                          </div>
                        </div>

                        <div className="request-message">
                          <div className="message-label">💬 Your Query:</div>
                          <p className="message-text">{query.query_message}</p>
                        </div>

                        {query.response && (
                          <div className="request-response">
                            <div className="message-label">📨 Agency Response:</div>
                            <p className="message-text response-text">{query.response}</p>
                          </div>
                        )}

                        <div className="request-contact">
                          <div className="contact-item-small">
                            <span className="contact-icon">📧</span>
                            <a href={`mailto:${query.agency?.user?.email}`} className="contact-link-small">
                              {query.agency?.user?.email || 'Email not available'}
                            </a>
                          </div>
                          {query.agency?.user?.phone && (
                            <div className="contact-item-small">
                              <span className="contact-icon">📱</span>
                              <a href={`tel:${query.agency?.user?.phone}`} className="contact-link-small">
                                {query.agency?.user?.phone}
                              </a>
                            </div>
                          )}
                          {query.agency?.website && (
                            <div className="contact-item-small">
                              <span className="contact-icon">🌐</span>
                              <a href={query.agency?.website} target="_blank" rel="noopener noreferrer" className="contact-link-small">
                                Visit Website
                              </a>
                            </div>
                          )}
                        </div>

                        {query.status === 'accepted' && (
                          <div className="request-actions">
                            {query.agency?.my_rating ? (
                              <div className="already-rated-badge">
                                ✓ Rated: {'⭐'.repeat(query.agency.my_rating)}
                              </div>
                            ) : (
                              <button 
                                className="rate-btn"
                                onClick={() => handleRateUser(
                                  query.agency?.user?.id,
                                  query.agency?.agency_name,
                                  'agency'
                                )}
                                title={`Rate ${query.agency?.agency_name}`}
                              >
                                <span className="btn-icon">⭐</span>
                                Rate Agency
                              </button>
                            )}
                          </div>
                        )}

                        <div className="request-date">
                          Sent: {new Date(query.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}

                    {/* Pagination controls */}
                    <div className="pagination">
                      <button className="prev-page" disabled>
                        ← Previous
                      </button>
                      <button className="next-page">
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Query Modal */}
      {showQueryModal && (
        <div className="modal-overlay" onClick={() => setShowQueryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Send Query to {selectedAgency?.agency_name}</h2>
            <p className="modal-subtitle">They will contact you via your registered email/phone</p>
            <textarea
              value={queryMessage}
              onChange={(e) => setQueryMessage(e.target.value)}
              placeholder="Enter your query..."
              rows="5"
              className="query-textarea"
            />
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowQueryModal(false)}
                disabled={sendingQuery}
              >
                Cancel
              </button>
              <button
                className="send-btn"
                onClick={handleSendQuery}
                disabled={sendingQuery}
              >
                {sendingQuery ? "Sending..." : "Send Query"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      <RatingModal
        show={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onSubmit={handleSubmitRating}
        userName={ratingTarget?.userName || ""}
        userRole={ratingTarget?.userRole || "agency"}
      />

      {/* Ratings View Modal */}
      <RatingsViewModal
        show={showRatingsViewModal}
        onClose={() => setShowRatingsViewModal(false)}
        userId={ratingsViewTarget?.userId}
        userName={ratingsViewTarget?.userName}
      />

      {/* Custom Alert Modal */}
      <CustomAlert
        show={alertState.show}
        onClose={closeAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />
    </div>
  );
}